import React, { useMemo } from 'react';
import { Box, Inscription } from '@common-fe/common-fe';

import { GENERIC_FORM_TYPES, LockboxStatus, LockboxType } from '@/modules/employer/components/Lockbox/lockbox.types';
import MultipleRequestsDetectedWarningMessage from '@/modules/employer/components/Lockbox/LockboxDetails/components/LockboxDividingMode/MultipleRequestsDetectedWarningMessage';
import { LockboxForm } from '@/modules/employer/components/Lockbox/LockboxDetails/components/LockboxInfo/LockboxForm/components';
import LockboxInfoContainer from '@/modules/employer/components/Lockbox/LockboxDetails/components/LockboxInfo/LockboxInfoContainer';
import {
  useLockboxClaimRepaymentFields,
  useLockboxGenericFormFields,
  useLockboxInboundTOAFields,
  useLockboxIOutboundTOAFields,
  useLockboxIReturnedCheckFields,
  useLockboxOutboundTOACloseAccountFields,
  useLockboxReturnMailFields,
} from '@/modules/employer/components/Lockbox/LockboxDetails/hooks';
import useLockboxDetailsStore from '@/modules/employer/components/Lockbox/LockboxDetails/stores/useLockboxDetails.store';
import { FeatureFlags, useFeatureFlag } from '@/modules/featureFlags';

import FailedToScanDocumentWarning from './components/FailedToScanDocumentWarning';

interface FormProps {
  isEditMode?: boolean;
}

const LockboxInboundTOAForm: React.FC<FormProps> = ({ isEditMode }) => {
  const { lockboxInboundTOAFields } = useLockboxInboundTOAFields();
  return <LockboxForm fields={lockboxInboundTOAFields} isEditMode={isEditMode} />;
};

const LockboxOutboundTOAForm: React.FC<FormProps> = ({ isEditMode }) => {
  const { lockboxIOutboundTOAFields } = useLockboxIOutboundTOAFields();
  return <LockboxForm fields={lockboxIOutboundTOAFields} isEditMode={isEditMode} />;
};

const LockboxOutboundTOACloseAccountForm: React.FC<FormProps> = ({ isEditMode }) => {
  const { lockboxOutboundTOACloseAccountFields } = useLockboxOutboundTOACloseAccountFields();
  return <LockboxForm fields={lockboxOutboundTOACloseAccountFields} isEditMode={isEditMode} />;
};

const LockboxClaimRepaymentForm: React.FC<FormProps> = ({ isEditMode }) => {
  const { lockboxClaimRepaymentFields } = useLockboxClaimRepaymentFields();
  return <LockboxForm fields={lockboxClaimRepaymentFields} isEditMode={isEditMode} />;
};

const LockboxGenericFormForm: React.FC<FormProps> = ({ isEditMode }) => {
  const { lockboxGenericFormFields } = useLockboxGenericFormFields();
  return <LockboxForm fields={lockboxGenericFormFields} isEditMode={isEditMode} />;
};

const LockboxReturnMailForm: React.FC<FormProps> = ({ isEditMode }) => {
  const { lockboxReturnMailFields } = useLockboxReturnMailFields(isEditMode);
  return <LockboxForm fields={lockboxReturnMailFields} isEditMode={isEditMode} />;
};

const LockboxReturnedCheckForm: React.FC<FormProps> = ({ isEditMode }) => {
  const { originalLockbox } = useLockboxDetailsStore();
  const { lockboxIReturnedCheckFields } = useLockboxIReturnedCheckFields({ lockbox: originalLockbox });
  return <LockboxForm fields={lockboxIReturnedCheckFields} isEditMode={isEditMode} />;
};

const LockboxInformationForm = () => {
  const { editableLockbox, originalLockbox, isEditMode, isDividingMode } = useLockboxDetailsStore();
  const lockboxType = editableLockbox?.lockboxType;
  const isReturnedChecksEnabled = useFeatureFlag(FeatureFlags.RETURNED_CHECKS_LOCKBOX);
  const isReturnedCheckWithFF = lockboxType === LockboxType.RETURNED_CHECK && isReturnedChecksEnabled;

  const isAvailable = useMemo(() => (lockboxType === LockboxType.INBOUND_TOA
  || lockboxType === LockboxType.OUTBOUND_TOA
  || lockboxType === LockboxType.RETURNED_CHECK
  || lockboxType === LockboxType.CLAIM_REPAYMENT
  || lockboxType === LockboxType.RETURNED_MAIL
  || (lockboxType && GENERIC_FORM_TYPES.includes(lockboxType))
  || editableLockbox?.isMultipleRecordsWarning)
  && originalLockbox?.lockboxStatus !== LockboxStatus.DIVIDED,
  [lockboxType, originalLockbox, editableLockbox]);

  const currentInformation = useMemo(() => {
    if (editableLockbox?.isMultipleRecordsWarning) {
      return <MultipleRequestsDetectedWarningMessage />;
    }

    if (originalLockbox?.ocr) {
      return (
        <>
          {lockboxType === LockboxType.INBOUND_TOA && <LockboxInboundTOAForm isEditMode={isEditMode} />}
          {lockboxType === LockboxType.OUTBOUND_TOA && <LockboxOutboundTOAForm isEditMode={isEditMode} />}
          {lockboxType === LockboxType.CLAIM_REPAYMENT && <LockboxClaimRepaymentForm isEditMode={isEditMode} />}
          {(lockboxType && GENERIC_FORM_TYPES.includes(lockboxType) && !isReturnedCheckWithFF) && <LockboxGenericFormForm isEditMode={isEditMode} />}
          {isReturnedCheckWithFF && <LockboxReturnedCheckForm isEditMode={isEditMode} />}
          {lockboxType === LockboxType.RETURNED_MAIL && <LockboxReturnMailForm isEditMode={isEditMode} />}
        </>
      );
    }

    return (
      <>
        <FailedToScanDocumentWarning />

        {lockboxType === LockboxType.OUTBOUND_TOA && (
          <Box margin={{ top: 'spacing12' }}>
            <LockboxOutboundTOACloseAccountForm isEditMode={isEditMode} />
          </Box>
        )}
      </>
    );
  }, [originalLockbox, lockboxType, isEditMode, editableLockbox, isReturnedCheckWithFF]);

  if (!isAvailable || isDividingMode) {
    return null;
  }

  return (
    <LockboxInfoContainer data-testid="lockbox-information-form" margin={{ top: 'spacing8' }}>
      <Inscription color="textSecondary" margin={{ bottom: 'spacing12' }}>
        Information located 
      </Inscription>

      {currentInformation}
    </LockboxInfoContainer>
  );
};

export default LockboxInformationForm;
